import React, { useCallback, useEffect, useRef, useState } from 'react';

import cs from 'classnames';

import { cn, ia } from 'lib/helpers/utility';
import { useWindowSize } from 'lib/hooks/useWindowSize';

import Button from '../Buttons/Button/Button';
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll';
import Popover from '../Popovers/Popover/Popover';

const ToggleItem = ({ item, active, onClick }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {item.split && <div className="h-full w-px bg-neutral-200" />}
      <Button.Group key={item.value} variant="tertiary" size="xs">
        <Button
          text={item.label}
          onClick={() => onClick(item)}
          active={active}
          tooltip={item.tooltip || item.label}
          tooltipColor="black"
          tooltipPlacement="bottom"
          isDisabled={item.disabled}
          icon={item.icon}
          className={cn(
            'whitespace-nowrap',
            (active || open) && 'border-neutral-200 bg-neutral-100',
            !item.actions && 'rounded-md',
            item.actions && 'max-w-[142px]'
          )}
          textClassName="text-ellipsis overflow-hidden"
        />
        {item.actions && (
          <Button.Dropdown
            icon="context-menu-vertical"
            className={cn(
              (active || open) && '!border-neutral-200 !bg-neutral-100',
              'border-l-0 hover:!bg-neutral-50'
            )}
            items={item.actions}
            iconColor="neutral"
            iconsShade={700}
            open={open}
            setOpen={setOpen}
          />
        )}
      </Button.Group>
    </>
  );
};

/**
 * @param {import('./lib/propTypes').ToggleSwitchProps} props
 */
const ToggleSwitch = ({
  items = [],
  onClick = () => {},
  active,
  disabled = false,
  count,
  horizontalScroll = false,
  loading
}) => {
  const [selected, setSelected] = useState(active);
  const containerRef = useRef(null);
  const [visibleItems, setVisibleItems] = useState(items);
  const [hiddenItems, setHiddenItems] = useState([]);
  const device = useWindowSize();
  const isMobile = device === 'mobile' || device === 'tablet-sm';
  const isTablet = device === 'tablet' || device === 'tablet-lg';

  const activeItem = active ?? selected;

  const calculateVisibleItems = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.parentElement.offsetWidth;
    let usedWidth = 0;
    const newVisibleItems = [];
    const newHiddenItems = [];

    items.forEach((item) => {
      const itemWidth = isMobile ? 150 : isTablet ? 120 : 100;
      if (usedWidth + itemWidth <= containerWidth) {
        newVisibleItems.push(item);
        usedWidth += itemWidth;
      } else {
        if (item.value === activeItem) {
          const lastVisibleItem = newVisibleItems.pop();
          newVisibleItems.push(item);
          if (lastVisibleItem) {
            newHiddenItems.push(lastVisibleItem);
          }
        } else {
          newHiddenItems.push(item);
        }
      }
    });

    setVisibleItems(newVisibleItems);
    setHiddenItems(newHiddenItems);
  }, [items, isMobile, isTablet, activeItem]);

  useEffect(() => {
    if (!horizontalScroll) {
      calculateVisibleItems();
      window.addEventListener('resize', calculateVisibleItems);
      return () => window.removeEventListener('resize', calculateVisibleItems);
    }
  }, [horizontalScroll, calculateVisibleItems]);

  const handleClick = (item) => {
    if (disabled || item.value === activeItem) return;

    setSelected(item.value);
    onClick(item);
    item.onClick?.(item);

    const isHiddenItem = hiddenItems.some((hiddenItem) => hiddenItem.value === item.value);
    if (isHiddenItem || !horizontalScroll) {
      const isInVisibleItems = visibleItems.some((visibleItem) => visibleItem.value === item.value);
      if (!isInVisibleItems) {
        setVisibleItems((prevVisible) => {
          if (prevVisible.length === 0) return prevVisible;
          return [...prevVisible.slice(0, -1), item];
        });

        setHiddenItems((prevHidden) => {
          const lastVisibleItem = visibleItems[visibleItems.length - 1];
          return [
            ...prevHidden.filter((hiddenItem) => hiddenItem.value !== item.value),
            lastVisibleItem
          ];
        });
      }
    }
  };

  if (!ia(items)) return null;

  const others = items.map((item) => ({
    ...item,
    textClassName: 'text-neutral-900',
    ...(item.actions && {
      component: ({ handleClose }) => (
        <Popover
          isFixed
          options={item.actions}
          placement="bottom-end"
          buttonClassName="border-none"
          icon="context-menu-horizontal"
          position="left"
          onOptionClick={handleClose}
          iconColor="neutral"
          iconsShade={700}
          textClassName="text-neutral-900"
          optionIconColor="neutral"
        />
      )
    }),
    onClick: () => handleClick(item)
  }));

  if (horizontalScroll) {
    return (
      <HorizontalScroll
        className={cs('rounded-md', {
          'cursor-not-allowed': disabled
        })}>
        {visibleItems.map((item) => (
          <ToggleItem
            key={item.value}
            item={item}
            active={active === item.value}
            disabled={disabled}
            onClick={handleClick}
            count={count}
            loading={loading}
          />
        ))}
      </HorizontalScroll>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cs('w-full max-w-full gap-2', 'flex items-center', {
        'cursor-not-allowed': disabled
      })}>
      {visibleItems.map((item) => (
        <ToggleItem
          key={item.value}
          item={item}
          active={active === item.value}
          disabled={disabled || item.disabled}
          onClick={handleClick}
          count={count}
          loading={loading}
        />
      ))}
      {hiddenItems.length > 0 && (
        <Popover
          buttonTheme="neutral"
          className="h-7"
          isFixed
          buttonClassName="rounded-md bg-transparent !min-h-7 !h-7"
          textClassName="text-neutral-800"
          optionGroupLabelClassName="text-neutral-800"
          options={others}
          placement="bottom-end"
          text="More..."
          isDropdown
          optionIconSize={16}
          optionIconColor="neutral"
          iconRightColor="neutral-800"
          iconsShade={600}
          icon=""
          menuPortalTarget={document.body}
          panelClassName="overflow-auto max-h-[500px]"
        />
      )}
    </div>
  );
};

export default ToggleSwitch;
